import { useTranslation } from "react-i18next";
import { NAMESPACES } from "@/i18n/constants";
import { DeviceTypeConfigurations, FirstPartyDeviceType } from "verdi-common";
import { Icon } from "@/components/icon";
import { ICON_KEYS } from "@/icons/icon-registry";
import { VERIFICATION_STATE } from "@/services/device-attributes/attributes/verification/definitions";
import type DeviceMapEntity from "../../services/map-management/map-entities/device-map-entity";
import GlobalOptions from "../../utils/global-options";

/**
 * Determine if the sprout verification should be shown for a given device
 */
export const shouldShowSproutVerification = ({ model }: { model: DeviceMapEntity["model"] }) => {
    const disabledByGlobalOptions = !GlobalOptions.showVerification;
    const deviceHasValves = DeviceTypeConfigurations[model?.type as FirstPartyDeviceType]?.valveCount > 0;
    const deviceHasSproutVerification = model?.sproutIrrigationVerification;

    // Make sure the verificationsByValveState is populated (not sure why it wouldnt be but we need to check)
    const verificationsByValveState = model?.sproutIrrigationVerification?.verificationsByValveState;
    const hasVerifications = verificationsByValveState ? Object.keys(verificationsByValveState).length > 0 : false;

    if (!deviceHasSproutVerification || disabledByGlobalOptions || !deviceHasValves || !hasVerifications) {
        return false;
    }

    return true;
};

export const vericationStatusIcons = {
    [VERIFICATION_STATE.SUCCESS]: ICON_KEYS.VERIFIED,
    [VERIFICATION_STATE.FAILED]: ICON_KEYS.ERROR,
    [VERIFICATION_STATE.INCOMPLETE]: ICON_KEYS.WARNING,
    [VERIFICATION_STATE.UNKNOWN]: ICON_KEYS.UNKNOWN,
};

export const varificationStatusClassNames = {
    [VERIFICATION_STATE.SUCCESS]: "text-white fill-[#43A047]",
    [VERIFICATION_STATE.FAILED]: "text-white fill-[#B70000]",
    [VERIFICATION_STATE.INCOMPLETE]: "text-white fill-[#FF9800]",
    [VERIFICATION_STATE.UNKNOWN]: "text-white fill-[#676767]",
};
/**
 * The translation key prefix for the verification status
 */
export const VERIFICATION_STATUS_TRANSLATION_NAMESPACE = NAMESPACES.ICONS;
export const VERIFICATION_STATUS_TRANSLATION_KEY_PREFIX = "verification-status";

export const verificationStatusTooltipTranslationKeys = {
    [VERIFICATION_STATE.SUCCESS]: "tooltip.passed",
    [VERIFICATION_STATE.FAILED]: "tooltip.failed",
    [VERIFICATION_STATE.INCOMPLETE]: "tooltip.incomplete",
    [VERIFICATION_STATE.UNKNOWN]: "tooltip.unknown",
};

export const verificationStatusLabelTranslationKeys = {
    [VERIFICATION_STATE.SUCCESS]: "label.passed",
    [VERIFICATION_STATE.FAILED]: "label.failed",
    [VERIFICATION_STATE.INCOMPLETE]: "label.incomplete",
    [VERIFICATION_STATE.UNKNOWN]: "label.unknown",
};

interface VerificationStatusIconProps {
    verificationState: VERIFICATION_STATE;
    includeLabel?: boolean;
    includeTooltip?: boolean;
}

/**
 * VerificationStatusIcon displays the verification status of a device
 */
export function VerificationStatusIcon({
    verificationState,
    includeLabel = true,
    includeTooltip = true,
}: VerificationStatusIconProps) {
    const { t } = useTranslation(NAMESPACES.ICONS, { keyPrefix: VERIFICATION_STATUS_TRANSLATION_KEY_PREFIX });

    const tooltip = t(verificationStatusTooltipTranslationKeys[verificationState]);
    const label = t(verificationStatusLabelTranslationKeys[verificationState]);

    return (
        <Icon
            icon={vericationStatusIcons[verificationState]}
            label={includeLabel ? label : undefined}
            tooltip={includeTooltip ? tooltip : undefined}
            iconClassName={varificationStatusClassNames[verificationState]}
        />
    );
}
